import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { FadeUp } from "../components/Reveal";
import { HEALTH_QUESTIONS, HC_AREA_LABELS, LIKERT } from "../data/content";

import { submitNetlifyForm } from "../lib/netlifyForm";
import { track } from "../lib/track";
const EASE = [0.16, 1, 0.3, 1];


// Scored in the browser: one question per area, average to 1dp, two lowest areas flagged.
const scoreAnswers = (answers) => {
    const scores = {};
    HEALTH_QUESTIONS.forEach((q) => { scores[q.category] = answers[q.id]; });
    const vals = Object.values(scores);
    const average = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
    const lowest_two = Object.keys(scores).sort((a, b) => scores[a] - scores[b]).slice(0, 2);
    return { scores, average, lowest_two };
};

const ScoreRow = ({ label, value, lowest, delay }) => (
    <div data-testid={`hc-result-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <div className="flex justify-between items-baseline mb-2">
            <span className={`text-sm font-medium ${lowest ? "text-gold-400" : "text-slate-300"}`}>
                {label} {lowest && <span className="font-mono text-[10px] tracking-widest uppercase ml-2 border border-gold-500/40 rounded-full px-2 py-0.5">Worth exploring</span>}
            </span>
            <span className="font-mono text-sm text-slate-200">{value} / 5</span>
        </div>
        <div className="h-2 rounded-full bg-navy-700 overflow-hidden">
            <motion.div
                className={`h-full rounded-full ${lowest ? "bg-gold-500" : "bg-gold-600/70"}`}
                initial={{ width: 0 }}
                animate={{ width: `${value * 20}%` }}
                transition={{ duration: 1, delay, ease: EASE }}
            />
        </div>
    </div>
);

export default function HealthCheck() {
    const [phase, setPhase] = useState("intro"); // intro | questions | results
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ name: "", email: "", phone: "", business: "", consent: false });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const allAnswered = HEALTH_QUESTIONS.every((q) => answers[q.id]);
    const answeredCount = Object.keys(answers).length;

    const submit = async () => {
        setSubmitting(true);
        const res = scoreAnswers(answers);
        let sent = false;
        try {
            // Only leads who leave an email and consent are sent to Netlify Forms.
            if (contact.consent && contact.email) {
                const fields = {
                    name: contact.name,
                    email: contact.email,
                    phone: contact.phone,
                    business_name: contact.business,
                    consent: "Yes",
                    average_score: `${res.average} / 5`,
                    focus_areas: res.lowest_two.map((c) => HC_AREA_LABELS[c]).join(", "),
                };
                Object.keys(HC_AREA_LABELS).forEach((c) => { fields[`score_${c}`] = res.scores[c]; });
                await submitNetlifyForm("business-health-check", fields);
                sent = true;
                track("generate_lead", { form: "business-health-check" });
            }
        } catch (err) {
            toast.error("Your results are below, but we couldn't send your details. Please book a conversation instead.");
        } finally {
            setResult({ ...res, sent });
            setPhase("results");
            track("health_check_complete", { average: res.average });
            window.scrollTo(0, 0);
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="health-check-page">
            <Seo
                title="Free Business Health Check | GrowVise"
                description="A free 2-minute self-assessment for NZ business owners: six questions across sales, margins, channels, operations, team and execution."
                path="/business-health-check"
            />

            <section className="bg-navy-950 pt-32 sm:pt-40 pb-20 min-h-screen" data-testid="hc-shell">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <AnimatePresence mode="wait">
                        {phase === "intro" && (
                            <motion.div key="intro" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.6, ease: EASE }}>
                                <p className="eyebrow mb-6">Free Business Health Check · 2 minutes</p>
                                <h1 className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl text-slate-50" data-testid="hc-title">
                                    Six questions. <span className="text-gold-500">An honest snapshot.</span>
                                </h1>
                                <p className="mt-6 text-lg text-slate-300 font-light leading-relaxed">
                                    Rate six statements about your sales, margins, channels, operations, team and execution. You'll get an instant average score and see which areas are worth exploring.
                                </p>
                                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                                    It's a self-assessment, not an audit — and it's never required before booking a conversation.
                                </p>
                                <div className="mt-10 flex flex-wrap gap-4">
                                    <button onClick={() => setPhase("questions")} className="btn-gold" data-testid="hc-start-btn">
                                        Start the Health Check <ArrowRight size={16} />
                                    </button>
                                    <Link to="/book-a-conversation" className="btn-outline" data-testid="hc-skip-btn">
                                        Skip — book a conversation
                                    </Link>
                                </div>
                            </motion.div>
                        )}

                        {phase === "questions" && (
                            <motion.div key="questions" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.5, ease: EASE }}>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold-400" data-testid="hc-progress-label">
                                        Business Health Check
                                    </p>
                                    <p className="font-mono text-xs text-slate-500">{answeredCount} / 6</p>
                                </div>
                                <div className="h-1 rounded-full bg-navy-700 mb-10 overflow-hidden">
                                    <motion.div className="h-full bg-gold-500" animate={{ width: `${(answeredCount / 6) * 100}%` }} transition={{ duration: 0.4 }} />
                                </div>

                                <div className="space-y-6">
                                    {HEALTH_QUESTIONS.map((q, i) => (
                                        <div key={q.id} className="card-surface p-6" data-testid={`hc-question-${q.id}`}>
                                            <p className="text-slate-200 font-medium leading-relaxed">
                                                <span className="font-mono text-xs text-gold-500 mr-3">0{i + 1}</span>
                                                {q.text}
                                            </p>
                                            <div className="mt-5 flex items-center gap-2">
                                                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 w-24 hidden sm:block">Disagree</span>
                                                <div className="flex-1 grid grid-cols-5 gap-2">
                                                    {LIKERT.map((l) => (
                                                        <button
                                                            key={l.value}
                                                            type="button"
                                                            onClick={() => setAnswers((a) => ({ ...a, [q.id]: l.value }))}
                                                            data-testid={`hc-answer-${q.id}-${l.value}`}
                                                            aria-label={`${l.label} (${l.value} of 5)`}
                                                            className={`h-11 rounded-xl border font-mono text-sm transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${
                                                                answers[q.id] === l.value
                                                                    ? "bg-gold-500 border-gold-500 text-navy-950 font-semibold"
                                                                    : "border-white/15 text-slate-400 hover:border-gold-500/50 hover:text-gold-400"
                                                            }`}
                                                        >
                                                            {l.value}
                                                        </button>
                                                    ))}
                                                </div>
                                                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 w-24 text-right hidden sm:block">Agree</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="card-surface p-6 mt-8" data-testid="hc-contact-card">
                                    <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-500">Optional · have Leo review your results</p>
                                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                                        Leave your details and Leo will personally review your answers and get in touch with two or three practical suggestions. Optional: your results show on screen either way.
                                    </p>
                                    <div className="grid sm:grid-cols-2 gap-4 mt-5">
                                        <input className="input-field" placeholder="Name (optional)" value={contact.name}
                                            onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="hc-name-input" />
                                        <input className="input-field" type="email" placeholder="Email (optional)" value={contact.email}
                                            onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="hc-email-input" />
                                        <input className="input-field" placeholder="Business name (optional)" value={contact.business}
                                            onChange={(e) => setContact({ ...contact, business: e.target.value })} data-testid="hc-business-input" />
                                        <input className="input-field" type="tel" placeholder="Phone (optional)" value={contact.phone}
                                            onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="hc-phone-input" />
                                    </div>
                                    <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="hc-consent-label">
                                        <input type="checkbox" className="mt-1 accent-[#D4AF37]" checked={contact.consent}
                                            onChange={(e) => setContact({ ...contact, consent: e.target.checked })} data-testid="hc-consent-checkbox" />
                                        <span className="text-xs text-slate-400 leading-relaxed">
                                            I agree to GrowVise storing these details and contacting me about my results. See the{" "}
                                            <Link to="/privacy" className="text-gold-400 hover:text-gold-300 underline underline-offset-2" data-testid="hc-privacy-link">Privacy Policy</Link>.
                                        </span>
                                    </label>
                                </div>

                                <div className="mt-8 flex justify-between items-center">
                                    <button onClick={() => setPhase("intro")} className="btn-outline px-5 py-3" data-testid="hc-back-btn">
                                        Back
                                    </button>
                                    <button
                                        onClick={submit}
                                        disabled={!allAnswered || submitting}
                                        className="btn-gold disabled:opacity-40 disabled:pointer-events-none"
                                        data-testid="hc-submit-btn"
                                    >
                                        {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                                        See my results <ArrowRight size={15} />
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {phase === "results" && result && (
                            <motion.div key="results" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} data-testid="hc-results">
                                <p className="eyebrow mb-6">Your Health Check result</p>
                                <div className="card-surface p-8 sm:p-10">
                                    <div className="flex flex-col sm:flex-row items-center gap-10">
                                        <div className="relative w-40 h-40 shrink-0" data-testid="hc-score-dial">
                                            <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                                                <circle cx="80" cy="80" r="64" fill="none" stroke="#142747" strokeWidth="10" />
                                                <motion.circle
                                                    cx="80" cy="80" r="64" fill="none" stroke="#D4AF37" strokeWidth="10" strokeLinecap="round"
                                                    strokeDasharray={2 * Math.PI * 64}
                                                    initial={{ strokeDashoffset: 2 * Math.PI * 64 }}
                                                    animate={{ strokeDashoffset: 2 * Math.PI * 64 * (1 - result.average / 5) }}
                                                    transition={{ duration: 1.6, ease: EASE }}
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="font-serif font-bold text-4xl text-slate-50">{result.average}</span>
                                                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-1">/ 5</span>
                                            </div>
                                        </div>
                                        <div className="flex-1 text-center sm:text-left">
                                            <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-500">What your answers suggest</p>
                                            <p className="mt-3 text-slate-300 leading-relaxed" data-testid="hc-summary">
                                                Your answers suggest opportunities worth exploring in{" "}
                                                <span className="text-gold-400 font-medium">
                                                    {result.lowest_two.map((c) => HC_AREA_LABELS[c]).join(" and ")}
                                                </span>
                                                .
                                            </p>
                                            <p className="mt-3 text-sm text-slate-500 leading-relaxed" data-testid="hc-disclaimer">
                                                This is a self-assessment, not an audited finding — a short conversation is the best way to test what it means for your business.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-10 space-y-5">
                                        {Object.keys(HC_AREA_LABELS).map((c, i) => (
                                            <ScoreRow key={c} label={HC_AREA_LABELS[c]} value={result.scores[c]} lowest={result.lowest_two.includes(c)} delay={0.3 + i * 0.1} />
                                        ))}
                                    </div>
                                </div>
                                {result.sent && (
                                    <div className="mt-6 flex items-center gap-3 text-sm text-slate-400" data-testid="hc-email-note">
                                        <MailCheck size={16} className="text-gold-500" />
                                        Thanks{contact.name ? ` ${contact.name.split(" ")[0]}` : ""}. Leo has your results and will be in touch at {contact.email} within one business day.
                                    </div>
                                )}
                                <div className="card-surface mt-8 p-6 sm:p-8 border-gold-500/30" data-testid="hc-next-step">
                                    <p className="font-serif text-xl sm:text-2xl text-slate-50">Talk through your two focus areas.</p>
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                                        A free 30-minute Growth Conversation turns this score into two or three practical next steps for your business. No obligation.
                                    </p>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-4">
                                    <Link to="/book-a-conversation" className="btn-gold" data-testid="hc-book-cta">
                                        Book a Growth Conversation <ArrowRight size={16} />
                                    </Link>
                                    <Link to="/how-we-help" className="btn-outline" data-testid="hc-explore-cta">
                                        Explore how we help
                                    </Link>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>
        </div>
    );
}
